import { useCallback, useEffect, useState } from "react";
import { Sparkles, Copy, CheckCircle2, RefreshCw, MessageSquareText, Star } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const TONES = [
  { value: "professionale", label: "Professionale" },
  { value: "amichevole", label: "Amichevole" },
  { value: "empatico", label: "Empatico" },
  { value: "scusa", label: "Di scuse" },
];

/**
 * Fase 6 (Parte B — AI Spark) — Inline AI Reply for a single review.
 *
 * Uses `POST /api/ai/reply` with `review_id` (ownership + Pro gating + quota
 * enforced server-side). The dialog surfaces the structured `{reply, language,
 * tone}` response and lets the user regenerate with a different tone or copy
 * the suggestion — nothing is auto-published to Google.
 */
export default function AiReplyDialog({ review, open, onOpenChange, onQuotaChanged }) {
  const [tone, setTone] = useState("professionale");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (t) => {
    if (!review?.review_id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/ai/reply", {
        review_id: review.review_id,
        tone: t || tone,
      });
      setResult(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail?.code === "ai_quota_exceeded") {
        setError(detail.message || `Quota AI mensile esaurita.`);
      } else if (status === 402) {
        setError("Serve un piano Pro attivo per usare AI Reply.");
      } else if (status === 503) {
        setError("Servizio AI momentaneamente non disponibile.");
      } else if (status === 404) {
        setError("Recensione non trovata.");
      } else {
        setError("Impossibile generare la risposta.");
      }
    } finally {
      setLoading(false);
      onQuotaChanged?.();
    }
  }, [review, tone, onQuotaChanged]);

  // Auto-generate when the dialog opens
  useEffect(() => {
    if (open && review?.review_id && !result && !loading) {
      run(tone);
    }
    if (!open) {
      // reset on close
      setResult(null);
      setError(null);
      setCopied(false);
      setTone("professionale");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, review?.review_id]);

  const copy = async () => {
    if (!result?.reply) return;
    try {
      await navigator.clipboard.writeText(result.reply);
      setCopied(true);
      toast.success("Risposta copiata");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const regenerate = (newTone) => {
    setTone(newTone);
    setResult(null);
    run(newTone);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="ai-reply-dialog"
        className="bg-[var(--bg-1)] border-[var(--border-c)] max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[var(--fg-1)] flex items-center gap-2">
            <MessageSquareText size={18} className="text-[#00F2F4]" />
            Rispondi con AI
          </DialogTitle>
        </DialogHeader>

        {/* Original review preview */}
        {review && (
          <div className="rounded-xl border border-[var(--border-c)] bg-[var(--bg-2)] p-4">
            <div className="flex items-center justify-between text-xs text-[var(--fg-3)] font-sans">
              <span className="font-semibold text-[var(--fg-1)]">{review.author_name || "Cliente"}</span>
              <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={11} className={i <= (review.rating || 0) ? "fill-[#E9A94E] text-[#E9A94E]" : "text-[var(--border-c)]"} strokeWidth={1.5} />
                ))}
              </span>
            </div>
            <p className="mt-2 text-sm text-[var(--fg-2)] leading-relaxed">{review.text}</p>
          </div>
        )}

        {/* Tone selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Tono</span>
          {TONES.map((t) => (
            <button
              key={t.value}
              onClick={() => regenerate(t.value)}
              disabled={loading}
              data-testid={`ai-reply-dialog-tone-${t.value}`}
              className={`text-xs font-sans px-3 py-1.5 rounded-full border ${
                tone === t.value
                  ? "border-[#00F2F4] bg-[#00F2F4]/10 text-[#00F2F4]"
                  : "border-[var(--border-c)] text-[var(--fg-2)] hover:border-[var(--fg-3)]"
              } disabled:opacity-50`}
              style={{ transition: "border-color 0.2s ease, background-color 0.2s ease" }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Reply / loading / error */}
        <div className="min-h-[120px] rounded-xl border border-[#00F2F4]/30 bg-[var(--bg-2)] p-4">
          {loading && (
            <div data-testid="ai-reply-dialog-loading" className="flex items-center gap-2 text-sm text-[var(--fg-3)]">
              <RefreshCw size={14} className="animate-spin" />
              <span>Generazione risposta con Gemini…</span>
            </div>
          )}
          {error && !loading && (
            <div data-testid="ai-reply-dialog-error" className="text-sm text-[#C6423B]">{error}</div>
          )}
          {result && !loading && (
            <div data-testid="ai-reply-dialog-result">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans mb-2">
                <span>Risposta suggerita</span>
                <span className="flex items-center gap-2">
                  <span data-testid="ai-reply-dialog-language">Lingua: {result.language}</span>
                  <span>·</span>
                  <span data-testid="ai-reply-dialog-tone-out">Tono: {result.tone}</span>
                </span>
              </div>
              <p data-testid="ai-reply-dialog-text" className="text-sm text-[var(--fg-1)] leading-relaxed whitespace-pre-wrap">
                {result.reply}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[10px] text-[var(--fg-3)] italic">
            Suggerimento — non viene pubblicato su Google.
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => run(tone)}
              disabled={loading || !review?.review_id}
              data-testid="ai-reply-dialog-regenerate-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] text-[var(--fg-1)] px-3 py-1.5 text-xs font-sans disabled:opacity-50"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <Sparkles size={12} /> Rigenera
            </button>
            <button
              onClick={copy}
              disabled={!result?.reply}
              data-testid="ai-reply-dialog-copy-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] disabled:opacity-50 text-[#0A0A0A] px-4 py-2 text-xs font-sans font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
              {copied ? "Copiato" : "Copia risposta"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
